import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import { Box, Divider, IconButton } from '@mui/material'
import { useQuery } from '@tanstack/react-query'
import { ReactComponent as DownloadLogo } from 'assets/analytics_download_logo.svg'
import { ReactComponent as SearchIcon } from 'assets/search.svg'
import { CattlesFilterStats } from 'components/cattles/cattlesPage/shared/CattlesFilterContent'
import Input from 'components/shared/textFields/Input'
import { useExportCowsListReport } from 'hook/query/useExportLamenessReport'
import { useDebounce } from 'hook/useDebounce'
import useCowsNextPage from 'hook/useNextPage'
import { IFiltersLamenessTable, LamenessStatus } from 'models'
import { useCallback, useEffect, useMemo, useState } from 'react'
import CattlesApiService from 'services/cattlesApiServices'
import { fetchLightGroupList } from 'store/groupsSlice'
import { useAppDispatch, useAppSelector } from 'store/hooks'
import { defaultInitialState } from 'store/lamenessFilterSlice'
import { COLORS } from 'styles/colors'
import { CardStandart } from 'ui/CardStandart'
import CustomPagination from 'ui/CustomPagination'
import Preloader from 'ui/Preloader'
import TooltipHover from 'ui/TooltipHover'
import { TypographyHeader } from 'ui/typography/TypographyHeader'
import { applyPageGroupIds } from '../shared/helper/applyPageGroupIds'
import { getCowsFilterStats } from '../shared/helper/getCowsFilterStats'
import { LAME_HEADS_ALL_SCORE_VALUES } from '../shared/helper/lameHeadsScoreOptions'
import AddColumnMenu from '../shared/filterElements/AddColumnMenu'
import LameHeadsFilterContent from '../shared/filterElements/LameHeadsFilterContent'
import { useLameHeadsColumns } from '../shared/hooks/useLameHeadsColumns'
import LameHeadsTable, { LameHeadsSortOrder } from '../shared/LameHeadsTable'

const PAGE_SIZE = 36
/** Новые даты «Последний замер» сверху. Кнопка колонки задаёт возрастание, повторный клик возвращает сюда. */
const DEFAULT_ORDERING = 'limpness_change_dt'

const COLUMN_SCORE_OPTIONS = LAME_HEADS_ALL_SCORE_VALUES
const LAME_HEADS_DEFAULT_FILTERS: IFiltersLamenessTable = defaultInitialState

const statusesToColumnScores = (statuses: LamenessStatus[]) =>
	statuses
		.map(status => Number(status))
		.filter(score => COLUMN_SCORE_OPTIONS.includes(score))

interface LameHeadsListProps {
	isActive: boolean
	pageGroupIds?: number[]
}

const LameHeadsList = ({ isActive, pageGroupIds }: LameHeadsListProps) => {
	const dispatch = useAppDispatch()
	const { permissions } = useAppSelector(state => state.authReducer)
	const { userData } = useAppSelector(state => state.userReducer)
	const { lightGroupList } = useAppSelector(state => state.groupsReducer)
	const {
		columns,
		getColumn,
		toggleColumnByTitle,
		resetColumns,
		isDefaultView,
		minSelected,
		maxSelected,
	} = useLameHeadsColumns()

	const [page, setPage] = useState(1)
	const [activeFilters, setActiveFilters] = useState<IFiltersLamenessTable>(
		LAME_HEADS_DEFAULT_FILTERS,
	)
	const [sortOrder, setSortOrder] = useState<LameHeadsSortOrder>(null)
	const [debouncedValue, handleChange, value] = useDebounce()

	const scoreFilter = useMemo(
		() => statusesToColumnScores(activeFilters.lameness.statuses),
		[activeFilters.lameness.statuses],
	)

	const listOrdering = sortOrder ?? DEFAULT_ORDERING

	const filtersWithPageGroups = useMemo(
		() => applyPageGroupIds(activeFilters, pageGroupIds),
		[activeFilters, pageGroupIds],
	)

	useEffect(() => {
		setPage(1)
	}, [pageGroupIds])

	const exportColumns = useMemo(() => {
		const selected = columns
			.filter(column => column.isShowColumn)
			.map(column => column.key)
		return Array.from(
			new Set(['tag_id', 'collar_num', ...selected, 'limpness_score_is_false']),
		)
	}, [columns])

	const {
		cowsList,
		totalCountPages,
		fetchingCowsList,
		loadedPage,
		isCowsListError,
	} = useCowsNextPage({
		params: filtersWithPageGroups,
		page,
		setPage,
		initialPageSize: PAGE_SIZE,
		isFetchCowsListEnabled: isActive,
		isFetchComplete: true,
		tag_id_search: debouncedValue,
		ordering: listOrdering,
	})

	const { isFetchReport, setIsFetchReport } = useExportCowsListReport({
		params: {
			...filtersWithPageGroups,
			tag_id_search: debouncedValue,
		},
		page,
		initialPageSize: PAGE_SIZE,
		ordering: listOrdering,
		columns: exportColumns,
	})

	const { data: filterSourceCows = [] } = useQuery({
		queryKey: ['lameHeadsFilterSource', debouncedValue],
		queryFn: async () => {
			const response = await CattlesApiService.getCowsList(
				undefined,
				undefined,
				1,
				10000,
				LAME_HEADS_DEFAULT_FILTERS,
				debouncedValue,
			)
			return response.data.results
		},
		enabled: isActive,
		staleTime: 50000,
	})

	const filterStats = useMemo(
		() =>
			getCowsFilterStats(filterSourceCows) satisfies CattlesFilterStats & {
				lastLamenessMeasurement: { start: number | ''; end: number | '' }
			},
		[filterSourceCows],
	)

	const availableGroups = useMemo(() => {
		if (lightGroupList?.length) {
			return lightGroupList
				.map(group => ({ id: group.id, title: group.title }))
				.sort((left, right) => left.title.localeCompare(right.title, 'ru'))
		}

		const groupsMap = new Map<number, string>()
		filterSourceCows.forEach(cow => {
			const groupId = cow.group?.id
			const groupTitle = cow.group?.title
			if (groupId != null && groupTitle) {
				groupsMap.set(groupId, groupTitle)
			}
		})
		return Array.from(groupsMap, ([id, title]) => ({ id, title }))
	}, [filterSourceCows, lightGroupList])

	const isFilterActive = useMemo(
		() =>
			JSON.stringify(activeFilters) !==
			JSON.stringify(LAME_HEADS_DEFAULT_FILTERS),
		[activeFilters],
	)

	useEffect(() => {
		if (!lightGroupList) {
			dispatch(fetchLightGroupList())
		}
	}, [dispatch, lightGroupList])

	useEffect(() => {
		setPage(1)
	}, [debouncedValue, activeFilters])

	const returnInitialProps = useCallback(() => {
		setActiveFilters(LAME_HEADS_DEFAULT_FILTERS)
		setPage(1)
	}, [])

	const handleScoreFilterChange = useCallback(
		(nextScores: number[] | ((prev: number[]) => number[])) => {
			setActiveFilters(prev => {
				const previousScores = statusesToColumnScores(prev.lameness.statuses)
				const resolvedScores =
					typeof nextScores === 'function'
						? nextScores(previousScores)
						: nextScores
				const normalizedScores = resolvedScores.filter(score =>
					COLUMN_SCORE_OPTIONS.includes(score),
				)

				return {
					...prev,
					lameness: {
						...prev.lameness,
						statuses: normalizedScores.map(
							score => String(score) as LamenessStatus,
						),
					},
				}
			})
		},
		[],
	)

	const handleSortOrder = (order: Exclude<LameHeadsSortOrder, null>) => {
		setSortOrder(currentOrder => (currentOrder === order ? null : order))
		setPage(1)
	}

	const showFatness =
		Boolean(permissions?.license_fatness) && !userData.isFatnessExpired

	return (
		<CardStandart
			sx={{
				display: 'flex',
				flexDirection: 'column',
				justifyContent: 'space-between',
				padding: '24px',
				width: '100%',
				maxWidth: '100%',
				minWidth: 0,
			}}
		>
			<Box
				display='flex'
				flexDirection='row'
				justifyContent='space-between'
				alignItems='center'
			>
				<Box display='flex' alignItems='center' gap='6px'>
					<TypographyHeader>Список голов</TypographyHeader>
					<TooltipHover
						title='Таблица содержит записи о последних баллах хромоты голов'
						placement='top-start'
						disableHoverBackground
					>
						<Box
							sx={{
								display: 'flex',
								color: COLORS.icon,
								cursor: 'pointer',
								'&:hover': { color: COLORS.main },
							}}
						>
							<InfoOutlinedIcon sx={{ fontSize: 18, color: 'inherit' }} />
						</Box>
					</TooltipHover>
				</Box>
				<Box display='flex' flexDirection='row' alignItems='center' gap='14px'>
					<Input
						width='330px'
						value={value}
						onChange={handleChange}
						label=''
						placeholder='Поиск по номеру бирки и ошейника'
						size='small'
						startIcon={<SearchIcon stroke={COLORS.icon} />}
						inputProps={{ style: { paddingLeft: '4px' } }}
					/>
					<AddColumnMenu
						onClick={toggleColumnByTitle}
						activeColumns={columns}
						isDefaultView={isDefaultView}
						onResetView={resetColumns}
						minSelected={minSelected}
						maxSelected={maxSelected}
					/>
					<LameHeadsFilterContent
						activeFilters={activeFilters}
						setActiveFilters={setActiveFilters}
						defaultFilters={LAME_HEADS_DEFAULT_FILTERS}
						isFilterActive={isFilterActive}
						returnInitialProps={returnInitialProps}
						availableStats={filterStats}
						tagIdSearch={debouncedValue}
						showFatness={showFatness}
						availableGroups={availableGroups}
					/>
					<TooltipHover
						title='Экспорт таблицы'
						placement='bottom-end'
						oneLine
						disableHoverBackground
					>
						<IconButton
							onClick={() => setIsFetchReport(true)}
							size='small'
							disabled={isFetchReport}
							sx={{
								borderRadius: '8px',
								'&:hover': { backgroundColor: COLORS.background },
							}}
						>
							{isFetchReport ? (
								<Preloader color={COLORS.icon} size={24} />
							) : (
								<DownloadLogo stroke={COLORS.icon} />
							)}
						</IconButton>
					</TooltipHover>
				</Box>
			</Box>
			<LameHeadsTable
				cattlesInfo={cowsList}
				fetchingCowsList={fetchingCowsList}
				sortOrder={sortOrder}
				onSortOrder={handleSortOrder}
				scoreFilter={scoreFilter}
				setScoreFilter={handleScoreFilterChange}
				getColumn={getColumn}
				page={page}
				loadedPage={loadedPage}
				pageSize={PAGE_SIZE}
				totalCount={isCowsListError ? 0 : totalCountPages}
				onPageChange={setPage}
				hasError={isCowsListError}
				showFilteredEmptyState={isFilterActive || Boolean(debouncedValue)}
			/>
			<Box display='flex' flexDirection='column' gap='16px'>
				<Divider />
				<CustomPagination
					onChangeFromServer={setPage}
					count={PAGE_SIZE}
					totalCount={isCowsListError ? 0 : totalCountPages}
					page={page}
				/>
			</Box>
		</CardStandart>
	)
}

export default LameHeadsList
